import { createRoot } from 'react-dom/client';
import { createElement } from 'react';
import { getAllStores } from './stores';
import { loadRemote } from './moduleFederation';

const importPromises = {};
let dictionaryPromise = null;

const componentLoaders = {
	CAW: () => loadRemote('CAW').then(remote => remote.CAW),
	Checkout: () => loadRemote('Checkout'),
	OpenTable: () => loadRemote('OpenTable'),
	Cart: () => loadRemote('Cart').then(remote => remote.Cart),
	CartStatus: () => loadRemote('Cart').then(remote => remote.CartStatus),
	ProductCard: () => loadRemote('Cart').then(remote => remote.ProductCard),
	ProductSignatureCardDetails: () =>
		loadRemote('Cart').then(remote => remote.ProductSignatureCardDetails),
	ProfileStatus: () => loadRemote('Cart').then(remote => remote.ProfileStatus),
	ProfileLinks: () => loadRemote('Cart').then(remote => remote.ProfileLinks),
	CurrencyDropdown: () => loadRemote('Cart').then(remote => remote.CurrencyDropdown),
	AddToCart: () => loadRemote('AddToCart').then(remote => remote.AddToCart),
	AddToCartTable: () => loadRemote('AddToCart').then(remote => remote.AddToCartTable),
	AccommodationsBookingFlow: () =>
		loadRemote('AccommodationsBookingFlow').then(remote => {
			return remote.AccommodationsBookingFlow;
		}),
	WebChatEmbed: () => loadRemote('WebChat'),
	ActivityCalendar: () =>
		loadRemote('ActivityCalendar').then(remote => {
			return remote.ActivityCalendar;
		}),
	UserPanel: () => loadRemote('UserPanel'),
};

const fetchDictionary = () => {
	if (!dictionaryPromise) {
		const language = document.querySelector('body')?.dataset?.language;
		if (language) {
			dictionaryPromise = fetch(
				`/alt/apps/fshr/feeds/dictionary?allTranslations=true&language=${language}`
			)
				.then(res =>
					res.json().then(json => {
						if (!json || json?.status === 'error') {
							throw new Error(json?.message || 'no data returned');
						}
						return json;
					})
				)
				.catch(error => {
					console.error(`Error loading dictionary: `, error);
					return {};
				});
		} else {
			console.error('Error loading dictionary: no language provided');
			dictionaryPromise = Promise.resolve({});
		}
	}
	return dictionaryPromise;
};

const initializePlaceholder = (componentName, originalPlaceholder, uniqueId) => {
	let unmount;
	let placeholder = originalPlaceholder;

	const mountAndRender = () => {
		if (
			!placeholder.classList.contains(`${componentName}-react-ssr-rendering`) &&
			!placeholder.classList.contains(`${componentName}-react-ssr-rendered`)
		) {
			importPromises['getStores'] = importPromises['getStores'] || getAllStores();
			importPromises['getStores'].then(allStores => {
				placeholder.classList.add(`${componentName}-react-ssr-rendering`);
				// grab the import promise or initialize it for the first time
				importPromises[componentName] =
					importPromises[componentName] || componentLoaders[componentName]();

				fetchDictionary().then(dictionary => {
					importPromises[componentName]
						.then(reactComponent => {
							const root = createRoot(placeholder, {
								identifierPrefix:
									placeholder.id ||
									`${componentName}-react-ssr-placeholder-${uniqueId}`,
							});
							let props = placeholder.dataset.props || placeholder.dataset;
							if (props) {
								//Object Map, attempt to parse any json that was previously stringified
								props = Object.fromEntries(
									Object.entries(props).map(([k, v]) => {
										let finalValue = v;
										try {
											finalValue = JSON.parse(finalValue);
										} catch {
											finalValue = v;
										}
										return [k, finalValue];
									})
								);
							}

							if (props.stores && props.stores.length) {
								props = {
									...props,
									...props.stores.reduce(
										(aggregate, storeName) => ({
											...aggregate,
											[storeName]: allStores[storeName]
												? allStores[storeName]()
												: undefined,
										}),
										{}
									),
								};
							}
							if (props.translations) {
								props.translations = dictionary || {};
							}
							root.render(createElement(reactComponent, props));
							placeholder.classList.add(`${componentName}-react-ssr-rendered`);
							placeholder.classList.remove(`${componentName}-react-ssr-rendering`);

							return () => root.unmount();
						})
						.catch(error => {
							console.error(`Error loading ${componentName}`, error);
						});
				});
			});
		}
	};

	const attachMoutingListeners = () => {
		$(placeholder).on('react.mount', () => {
			unmount = mountAndRender();
		});

		$(placeholder).on('react.unmount', () => {
			//destroy all child elements and remove rendered class, then clone the div and replace it
			// only doing this to get rid a warning react sends out about reusing a root element.
			if (unmount) {
				unmount();
			}
			placeholder.classList.remove(`${componentName}-react-ssr-rendered`);
			const newPlaceholder = placeholder.cloneNode();
			placeholder.replaceWith(newPlaceholder);
			placeholder = newPlaceholder;
			attachMoutingListeners();
		});
	};

	if (!placeholder.classList.contains('lazy-render')) {
		unmount = mountAndRender();
	}

	attachMoutingListeners();
};

window.addEventListener('react.register', e => {
	initializePlaceholder(e.target.dataset.componentName, e.target, new Date().getTime());
});

const initializeAll = () => {
	Object.keys(componentLoaders).forEach(componentName => {
		const placeholders = document.querySelectorAll(`.${componentName}-react-ssr-placeholder`);
		if (placeholders.length > 0) {
			fetchDictionary();
			placeholders.forEach((originalPlaceholder, i) => {
				initializePlaceholder(componentName, originalPlaceholder, i);
			});
		}
	});
};
export default function () {
	initializeAll();
}
